(() => {
  if (customElements.get('dn-delivery-estimate')) return;

  const dayLength = 86400000;
  const calendars = new Map();
  const helsinkiClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Helsinki', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  });
  const isoDay = (day) => new Date(day).toISOString().slice(0, 10);
  const parseDay = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
    const result = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(result) && isoDay(result) === value ? result : NaN;
  };

  async function loadCalendar(url) {
    if (!url) return null;
    if (!calendars.has(url)) {
      calendars.set(url, fetch(url).then((response) => {
        if (!response.ok) throw new Error('Calendar unavailable');
        return response.json();
      }).catch((error) => {
        calendars.delete(url);
        throw error;
      }));
    }
    return calendars.get(url);
  }

  class DnDeliveryEstimate extends HTMLElement {
    static observedAttributes = ['data-country', 'data-min-days', 'data-max-days', 'data-processing-days', 'data-calendar-url', 'data-locale'];

    static calculate(now, country, minimum, maximum, processingDays, calendar) {
      if (!(now instanceof Date) || !Number.isFinite(now.getTime()) || calendar?.version !== 1 ||
          !Array.isArray(calendar.countries?.[country]) || !Array.isArray(calendar.dispatchClosures) ||
          !Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 1 || maximum < minimum || maximum > 30 ||
          ![0, 1].includes(processingDays)) return null;

      const parts = Object.fromEntries(helsinkiClock.formatToParts(now).map(({ type, value }) => [type, value]));
      const today = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
      const first = parseDay(calendar.from);
      const last = parseDay(calendar.through);
      if (!Number.isFinite(first) || !Number.isFinite(last) || today < first || today > last) return null;

      const dispatchClosures = new Set(calendar.dispatchClosures);
      const transitClosures = new Set([...calendar.dispatchClosures, ...calendar.countries[country]]);
      const isBusinessDay = (day, closures) => {
        const weekday = new Date(day).getUTCDay();
        return weekday !== 0 && weekday !== 6 && !closures.has(isoDay(day));
      };
      const advance = (start, count, closures) => {
        let day = start;
        for (let elapsed = 0; elapsed < 366 && count > 0; elapsed += 1) {
          day += dayLength;
          if (day > last) return null;
          if (isBusinessDay(day, closures)) count -= 1;
        }
        return count === 0 ? day : null;
      };

      // Rollover and processing share one dispatch allowance; transit starts after dispatch (day zero).
      const rolloverDays = Number(parts.hour) >= 16 || !isBusinessDay(today, dispatchClosures) ? 1 : 0;
      const dispatch = advance(today, Math.max(rolloverDays, processingDays), dispatchClosures);
      if (dispatch === null) return null;
      const earliest = advance(dispatch, minimum, transitClosures);
      const latest = advance(dispatch, maximum, transitClosures);
      if (earliest === null || latest === null) return null;
      return { earliest: isoDay(earliest), latest: isoDay(latest), dispatch: isoDay(dispatch), year: Number(parts.year) };
    }

    connectedCallback() {
      this.fallback ??= this.textContent.trim();
      this.refreshOnReturn ??= () => {
        if (!document.hidden) this.render();
      };
      document.addEventListener('visibilitychange', this.refreshOnReturn);
      window.addEventListener('pageshow', this.refreshOnReturn);
      this.render();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.revision = (this.revision || 0) + 1;
      document.removeEventListener('visibilitychange', this.refreshOnReturn);
      window.removeEventListener('pageshow', this.refreshOnReturn);
    }

    attributeChangedCallback() {
      if (this.isConnected && this.fallback !== undefined) this.render();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (!document.hidden) this.render();
        this.schedule();
      }, 60000 - (Date.now() % 60000) + 25);
    }

    async render() {
      const revision = this.revision = (this.revision || 0) + 1;
      try {
        const calendar = await loadCalendar(this.dataset.calendarUrl);
        if (!this.isConnected || revision !== this.revision) return;
        const range = DnDeliveryEstimate.calculate(new Date(), this.dataset.country,
          Number(this.dataset.minDays), Number(this.dataset.maxDays), Number(this.dataset.processingDays), calendar);
        if (!range) {
          this.restoreFallback();
          return;
        }
        const locale = this.dataset.locale?.startsWith('en') ? 'en-GB' : (this.dataset.locale || 'en-GB');
        const options = { day: 'numeric', month: 'short', timeZone: 'UTC' };
        if (Number(range.earliest.slice(0, 4)) !== range.year || Number(range.latest.slice(0, 4)) !== range.year) options.year = 'numeric';
        let formatter;
        try { formatter = new Intl.DateTimeFormat(locale, options); }
        catch { formatter = new Intl.DateTimeFormat('en-GB', options); }
        const startDate = new Date(parseDay(range.earliest));
        const endDate = new Date(parseDay(range.latest));
        const rangeParts = typeof formatter.formatRangeToParts === 'function'
          ? formatter.formatRangeToParts(startDate, endDate)
          : [
            { source: 'startRange', value: formatter.format(startDate) },
            { source: 'shared', value: ' – ' },
            { source: 'endRange', value: formatter.format(endDate) }
          ];
        if (locale === 'en-GB' && range.earliest.slice(0, 7) === range.latest.slice(0, 7)) {
          rangeParts.forEach((part) => {
            if (part.source === 'shared' && part.value.trim() === '–') part.value = '–';
          });
        }
        const text = rangeParts.map(({ value }) => value).join('');
        if (this.textContent !== text) {
          const groups = [];
          rangeParts.forEach(({ source, value }) => {
            const partSource = range.earliest === range.latest ? 'startRange' : source;
            if (groups.at(-1)?.source === partSource) groups.at(-1).value += value;
            else groups.push({ source: partSource, value });
          });
          this.replaceChildren(...groups.map(({ source, value }) => {
            if (source === 'shared') return document.createTextNode(value);
            const time = document.createElement('time');
            time.dateTime = source === 'startRange' ? range.earliest : range.latest;
            time.textContent = value;
            return time;
          }));
        }
        this.dataset.ready = 'true';
      } catch {
        if (this.isConnected && revision === this.revision) this.restoreFallback();
      }
    }

    restoreFallback() {
      if (this.textContent !== this.fallback) this.textContent = this.fallback;
      delete this.dataset.ready;
    }
  }

  customElements.define('dn-delivery-estimate', DnDeliveryEstimate);
})();
